import React from 'react';
import { ReservationFieldDef } from '../../interfaces';

interface Props {
    field: ReservationFieldDef;
    value: unknown;
    onChange: (key: string, value: unknown) => void;
    /**
     * Mark the answer as one that outlives this booking. Only worth showing once
     * a guest is linked, since a walk-in has no profile to save it to.
     */
    profileBacked?: boolean;
}

/**
 * Renders one admin-defined reservation field, so adding a field type here is
 * the only change needed to support it wherever the form appears.
 */
const ReservationFieldInput: React.FC<Props> = ({ field, value, onChange, profileBacked }) => {
    const set = (v: unknown) => onChange(field.key, v);
    const id = `resv-fld-${field.key}`;
    const profileChip = profileBacked
        ? <span className='resv-onprofile' title="Kept on the guest's profile for next time">On profile</span>
        : null;

    if (field.type === 'checkbox') {
        return (
            <label className='resv-check' htmlFor={id}>
                <input
                    id={id}
                    type='checkbox'
                    checked={value === true}
                    onChange={(e) => set(e.target.checked)}
                />
                <span className='resv-check__label'>
                    {field.label}
                    {profileChip}
                    {field.helpText && <span className='resv-check__help'>{field.helpText}</span>}
                </span>
            </label>
        );
    }

    const control = (() => {
        switch (field.type) {
            case 'textarea':
                return (
                    <textarea
                        id={id}
                        className='resv-input resv-input--area'
                        rows={2}
                        value={(value as string) || ''}
                        onChange={(e) => set(e.target.value)}
                        placeholder={field.helpText}
                    />
                );
            case 'select':
                return (
                    <select
                        id={id}
                        className='resv-input resv-select'
                        value={(value as string) || ''}
                        onChange={(e) => set(e.target.value || undefined)}
                    >
                        <option value=''>Not set</option>
                        {(field.options || []).map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                );
            case 'multiselect': {
                const selected = Array.isArray(value) ? (value as string[]) : [];
                return (
                    <div className='resv-multi'>
                        {(field.options || []).map(o => {
                            const on = selected.includes(o);
                            return (
                                <button
                                    key={o}
                                    type='button'
                                    className={`resv-tag${on ? ' is-on' : ''}`}
                                    onClick={() => set(on ? selected.filter(x => x !== o) : [...selected, o])}
                                >{o}</button>
                            );
                        })}
                    </div>
                );
            }
            case 'number':
                return (
                    <input
                        id={id}
                        type='number'
                        className='resv-input'
                        value={value == null ? '' : String(value)}
                        onChange={(e) => set(e.target.value === '' ? undefined : Number(e.target.value))}
                    />
                );
            default:
                return (
                    <input
                        id={id}
                        type={field.type === 'phone' ? 'tel' : field.type}
                        className='resv-input'
                        value={(value as string) || ''}
                        onChange={(e) => set(e.target.value)}
                        placeholder={field.helpText}
                    />
                );
        }
    })();

    return (
        <div className='resv-field'>
            <label className='resv-label' htmlFor={id}>
                {field.label}
                {field.required && <span className='resv-req'>*</span>}
                {profileChip}
            </label>
            {control}
        </div>
    );
};

export default ReservationFieldInput;
